import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { School, Users, Trophy, Target, Mail, Phone, Building } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { partnershipAPI } from '../services/api';
import { toast } from 'sonner';

const SchoolPartnerships = ({ user, onLogout }) => {
  const [formData, setFormData] = useState({
    school_name: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await partnershipAPI.create(formData);
      toast.success('Partnership inquiry submitted successfully!');
      setFormData({
        school_name: '',
        contact_person: '',
        contact_email: '',
        contact_phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Partnership error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navigation user={user} onLogout={onLogout} />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
              <School className="w-10 h-10 text-purple-700" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
              School Swimming Programs
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Partner with Ghana's leading aquatics provider to bring world-class swimming instruction to your students.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Partner With Us</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive aquatics programs designed specifically for educational institutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: School,
                title: 'Curriculum Integration',
                description: 'Programs aligned with educational objectives and physical education standards',
              },
              {
                icon: Users,
                title: 'Professional Staff',
                description: 'Certified instructors experienced in working with school-age children',
              },
              {
                icon: Trophy,
                title: 'Competitive Opportunities',
                description: 'Inter-school competitions and swim meets to showcase student progress',
              },
              {
                icon: Target,
                title: 'Safety First',
                description: 'Comprehensive safety protocols and risk management procedures',
              },
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border-2 border-slate-100 shadow-lg card-hover"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-purple-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Options */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Program Options</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Flexible programs designed to meet your school's unique needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'PE Integration',
                features: [
                  'Regular swimming lessons as part of PE curriculum',
                  'Progressive skill development',
                  'Assessment and reporting',
                  'Flexible scheduling',
                ],
              },
              {
                title: 'After-School Club',
                features: [
                  'Optional swimming club for interested students',
                  'Competitive training track',
                  'Team building activities',
                  'Competition participation',
                ],
              },
              {
                title: 'Learn-to-Swim Program',
                features: [
                  'Intensive courses during term breaks',
                  'Water safety certification',
                  'Small group instruction',
                  'Family involvement options',
                ],
              },
            ].map((program, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-xl"
                data-testid={`program-card-${i}`}
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-6">{program.title}</h3>
                <ul className="space-y-3">
                  {program.features.map((feature, j) => (
                    <li key={j} className="flex items-start">
                      <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                      </div>
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Start a Partnership</h2>
            <p className="text-xl text-slate-600">
              Let's discuss how we can bring exceptional swimming education to your school
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border-2 border-slate-100"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="school_name">
                  <Building className="inline w-4 h-4 mr-2" />
                  School Name
                </Label>
                <Input
                  id="school_name"
                  value={formData.school_name}
                  onChange={(e) => handleChange('school_name', e.target.value)}
                  required
                  data-testid="school-name-input"
                  placeholder="e.g., Ghana International School"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => handleChange('contact_person', e.target.value)}
                    required
                    data-testid="contact-person-input"
                    placeholder="Principal/Director name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">
                    <Phone className="inline w-4 h-4 mr-2" />
                    Phone Number
                  </Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    required
                    data-testid="contact-phone-input"
                    placeholder="+233 XX XXX XXXX"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact_email">
                  <Mail className="inline w-4 h-4 mr-2" />
                  Email Address
                </Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  required
                  data-testid="contact-email-input"
                  placeholder="contact@school.edu.gh"
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  required
                  data-testid="message-input"
                  rows={6}
                  placeholder="Tell us about your school, number of students, and what you're looking for in a swimming program..."
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary py-6 text-lg"
                disabled={loading}
                data-testid="submit-partnership-btn"
              >
                {loading ? 'Sending...' : 'Submit Partnership Inquiry'}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SchoolPartnerships;
